if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      variantChangeUnsubscriber = undefined;

      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.roseplyGroupOptionIndexes = (this.dataset.roseplyGroupOptionIndexes || '')
          .split(',')
          .map((value) => Number.parseInt(value, 10))
          .filter((value) => Number.isInteger(value));
        try {
          this.roseplySelectedOptions = JSON.parse(this.dataset.roseplySelectedOptions || '[]');
        } catch (_) {
          this.roseplySelectedOptions = [];
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.roseplyGalleryEnabled()) return;

        this.roseplyFilterMedia(
          this.dataset.roseplySelectedGroup,
          this.dataset.roseplyPrimaryGroup,
          this.roseplySelectedOptions
        );

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;

          const variantOptions = event.data.variant?.options || [];
          const selectedGroups = this.roseplyGroupOptionIndexes
            .map((index) => this.roseplyHandleize(variantOptions[index]))
            .filter(Boolean);
          const selectedGroup = selectedGroups.join('-');
          const primaryGroup = selectedGroups[0] || '';
          this.roseplyFilterMedia(selectedGroup, primaryGroup, variantOptions);
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]:not([hidden])') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail?.parentElement?.firstChild !== activeThumbnail &&
              activeThumbnail?.parentElement?.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      roseplyGalleryEnabled() {
        return Array.from(this.elements.viewer.querySelectorAll('[data-roseply-group]')).some(
          (item) => !['shared', 'ungrouped'].includes(item.dataset.roseplyGroup)
        );
      }

      roseplyHandleize(value) {
        if (!value) return '';

        return value
          .toString()
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      roseplyCandidateGroups(optionValues = []) {
        const groups = optionValues.map((value) => this.roseplyHandleize(value)).filter(Boolean);
        if (!groups.length) return [];

        const candidates = [];
        const maxMask = 1 << Math.min(groups.length, 8);
        for (let mask = 1; mask < maxMask; mask += 1) {
          const selected = [];
          for (let index = 0; index < groups.length; index += 1) {
            if (mask & (1 << index)) selected.push(groups[index]);
          }
          if (selected.length) candidates.push({ group: selected.join('-'), specificity: selected.length, mask });
        }

        return candidates
          .sort((a, b) => b.specificity - a.specificity || a.mask - b.mask)
          .map(({ group }) => group)
          .filter((group, index, array) => array.indexOf(group) === index);
      }

      roseplyFilterMedia(selectedGroup, primaryGroup = '', optionValues = []) {
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id][data-roseply-group]'));
        if (!mediaItems.length) return;

        const exactGroupExists = selectedGroup && mediaItems.some((item) => item.dataset.roseplyGroup === selectedGroup);
        const primaryGroupExists = primaryGroup && mediaItems.some((item) => item.dataset.roseplyGroup === primaryGroup);
        let resolvedGroup = exactGroupExists ? selectedGroup : primaryGroupExists ? primaryGroup : '';

        // Universal fallback: try the current variant's option values directly. This makes
        // ALT groups such as "January - Garnet | image" work even when a product template
        // was created later or still contains an older gallery option-name setting.
        if (!resolvedGroup) {
          const candidates = this.roseplyCandidateGroups(optionValues);
          resolvedGroup = candidates.find((candidate) =>
            mediaItems.some((item) => item.dataset.roseplyGroup === candidate)
          ) || '';
        }

        // If no ALT group maps to the current variant, preserve Shopify's normal gallery.
        if (!resolvedGroup) {
          this.roseplyShowAllMedia(mediaItems);
          return;
        }

        const visibleMediaIds = new Set();

        mediaItems.forEach((item) => {
          const shouldShow = ['shared', resolvedGroup].includes(item.dataset.roseplyGroup);
          item.hidden = !shouldShow;

          if (shouldShow) visibleMediaIds.add(item.dataset.mediaId);
        });

        // Keep the gallery in Shopify admin media order instead of variant-featured order.
        // Shopify's section response promotes the selected variant's featured media to the
        // front; without a stable order this makes later variants (e.g. April) open on the
        // variant thumbnail instead of the first image in that month's ALT group.
        const roseplyMediaOrder = (item) => {
          const order = Number.parseInt(item.dataset.roseplyOrder || '', 10);
          return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
        };

        const orderedVisibleMedia = [
          ...mediaItems
            .filter((item) => item.dataset.roseplyGroup === resolvedGroup && !item.hidden)
            .sort((a, b) => roseplyMediaOrder(a) - roseplyMediaOrder(b)),
          ...mediaItems
            .filter((item) => item.dataset.roseplyGroup === 'shared' && !item.hidden)
            .sort((a, b) => roseplyMediaOrder(a) - roseplyMediaOrder(b)),
        ];

        this.roseplyOrderVisibleMedia(orderedVisibleMedia);
        this.roseplySyncThumbnails(visibleMediaIds, orderedVisibleMedia);
        this.roseplyUpdateCounter(visibleMediaIds.size);
        this.elements.viewer.resetPages?.();

        const firstGroupMedia = orderedVisibleMedia.find(
          (item) => item.dataset.roseplyGroup === resolvedGroup
        );
        const firstVisibleMedia = firstGroupMedia || orderedVisibleMedia[0];

        if (firstVisibleMedia) this.setActiveMedia(firstVisibleMedia.dataset.mediaId, false);
      }

      roseplyShowAllMedia(mediaItems) {
        const visibleMediaIds = new Set();

        mediaItems.forEach((item) => {
          item.hidden = false;
          visibleMediaIds.add(item.dataset.mediaId);
        });

        this.roseplySyncThumbnails(visibleMediaIds);
        this.roseplyUpdateCounter(visibleMediaIds.size);
        this.elements.viewer.resetPages?.();
      }

      roseplyOrderVisibleMedia(orderedVisibleMedia) {
        if (!orderedVisibleMedia.length) return;

        const mediaList = orderedVisibleMedia[0].parentElement;
        orderedVisibleMedia.forEach((item) => mediaList.appendChild(item));
      }

      roseplySyncThumbnails(visibleMediaIds, orderedVisibleMedia = []) {
        if (!this.elements.thumbnails) return;

        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));

        thumbnails.forEach((thumbnail) => {
          thumbnail.hidden = !visibleMediaIds.has(thumbnail.dataset.target);
        });

        if (orderedVisibleMedia.length) {
          const thumbnailMap = new Map(thumbnails.map((thumbnail) => [thumbnail.dataset.target, thumbnail]));
          const thumbnailList = thumbnails[0]?.parentElement;

          orderedVisibleMedia.forEach((item) => {
            const thumbnail = thumbnailMap.get(item.dataset.mediaId);
            if (thumbnail && thumbnailList) thumbnailList.appendChild(thumbnail);
          });
        }

        this.elements.thumbnails.resetPages?.();
      }

      roseplyUpdateCounter(visibleCount) {
        const total = this.querySelector('.slider-counter--total');
        if (total) total.textContent = visibleCount;
      }
    }
  );
}
